// src/components/HomePage/HomePage.jsx

import React, { useEffect, useMemo, useState } from 'react';
import './HomePage.scss';

import HomeHelmet from '../../components/Sections/HomeHelmet/HomeHelmet';
import HomeHeader from '../../components/Sections/HomeHeader/HomeHeader';
import HeroSection from '../../components/Sections/HeroSection/HeroSection';
import ClubStatementSection from '../../components/Sections/ClubStatementSection/ClubStatementSection';
import HowItWorksSection from '../../components/Sections/HowItWorksSection/HowItWorksSection';
import ScoringExperienceSection from '../../components/Sections/ScoringExperienceSection/ScoringExperienceSection';
import GroupsExperienceSection from '../../components/Sections/GroupsExperienceSection/GroupsExperienceSection';
import TournamentExperienceSection from '../../components/Sections/TournamentExperienceSection/TournamentExperienceSection';
import SeasonExperienceSection from '../../components/Sections/SeasonExperienceSection/SeasonExperienceSection';
import MembershipIdentitySection from '../../components/Sections/MembershipIdentitySection/MembershipIdentitySection';
import EventsSection from '../../components/Sections/EventsSection/EventsSection';
import CoverageSection from '../../components/Sections/CoverageSection/CoverageSection';
import PricingSection from '../../components/Sections/PricingSection/PricingSection';
import LaunchStatusSection from '../../components/Sections/LaunchStatusSection/LaunchStatusSection.jsx';
import FAQSection from '../../components/Sections/FAQSection/FAQSection';
import ContactSection from '../../components/Sections/ContactSection/ContactSection';
import FooterSection from '../../components/Sections/FootSection/FooterSection';

export default function HomePage() {
  const SITE_URL = 'https://www.thetourgolfers.com';

  const API_BASE_URL = useMemo(() => {
    const envUrl = process.env.REACT_APP_API_URL;

    if (!envUrl) {
      return '';
    }

    return envUrl.endsWith('/') ? envUrl.slice(0, -1) : envUrl;
  }, []);

  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  const [formData, setFormData] = useState({
    inquiryType: 'founding_membership',
    fullName: '',
    email: '',
    city: '',
    message: '',
    ttg_middle_name_check: ''
  });

  const [formStatus, setFormStatus] = useState({
    loading: false,
    success: '',
    error: ''
  });

  useEffect(() => {
    document.body.classList.toggle('menu-open', mobileMenuOpen);

    return () => {
      document.body.classList.remove('menu-open');
    };
  }, [mobileMenuOpen]);

  const closeMobileMenu = () => {
    setMobileMenuOpen(false);
  };

  const handleInputChange = (event) => {
    const { name, value } = event.target;

    setFormData((prev) => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSubmit = async (event) => {
    event.preventDefault();

    setFormStatus({
      loading: true,
      success: '',
      error: ''
    });

    try {
      const response = await fetch(`${API_BASE_URL}/contact`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          inquiryType: formData.inquiryType,
          fullName: formData.fullName.trim(),
          email: formData.email.trim(),
          city: formData.city.trim(),
          message: formData.message.trim(),
          sourcePage: 'homepage',
          ttg_middle_name_check: formData.ttg_middle_name_check
        })
      });

      const contentType = response.headers.get('content-type') || '';

      const data = contentType.includes('application/json')
        ? await response.json()
        : null;

      if (!response.ok) {
        throw new Error(
          data?.message || 'Unable to send your request right now.'
        );
      }

      setFormStatus({
        loading: false,
        success:
          'Thanks — your request has been received. We will be in touch soon.',
        error: ''
      });

      setFormData({
        inquiryType: 'founding_membership',
        fullName: '',
        email: '',
        city: '',
        message: '',
        ttg_middle_name_check: ''
      });
    } catch (error) {
      setFormStatus({
        loading: false,
        success: '',
        error:
          error.message || 'Something went wrong sending your request.'
      });
    }
  };

  return (
    <div className="home-page">
      <HomeHelmet siteUrl={SITE_URL} />

      <a href="#main" className="skip-link">
        Skip to content
      </a>

      <HomeHeader
        mobileMenuOpen={mobileMenuOpen}
        setMobileMenuOpen={setMobileMenuOpen}
        closeMobileMenu={closeMobileMenu}
      />

      <main id="main">
        <HeroSection />
        <ClubStatementSection />
        <HowItWorksSection />
        <ScoringExperienceSection />
        <GroupsExperienceSection />
        <TournamentExperienceSection />
        <SeasonExperienceSection />
        <MembershipIdentitySection />
        <EventsSection />
        <CoverageSection />
        <PricingSection />
        <LaunchStatusSection />

        <FAQSection />

        <ContactSection
          formData={formData}
          formStatus={formStatus}
          handleInputChange={handleInputChange}
          handleSubmit={handleSubmit}
        />
      </main>

      <FooterSection />

      <a className="mobile-join-bar" href="#contact">
        <span>Founding Membership · 2027</span>
        <strong>Request Information</strong>
      </a>
    </div>
  );
}